import * as React from "react"
import { useIsMobile } from "@/hooks/use-mobile"

type AlertType = "success" | "error"

interface AlertData {
  id: string
  type: AlertType
  title?: string
  message: string
  isVisible: boolean
}

const listeners: Array<(alert: AlertData | null) => void> = []
let memoryAlert: AlertData | null = null

function dispatch(alert: AlertData | null) {
  memoryAlert = alert
  listeners.forEach((listener) => {
    listener(memoryAlert)
  })
}

let alertCount = 0

function generateId() {
  alertCount = (alertCount + 1) % Number.MAX_SAFE_INTEGER
  return alertCount.toString()
}

interface ShowAlertOptions {
  type: AlertType
  title?: string
  message: string
}

function showAlert({ type, title, message }: ShowAlertOptions) {
  const id = generateId()
  
  const alert: AlertData = {
    id,
    type,
    title,
    message,
    isVisible: true
  }
  
  dispatch(alert)
  
  // Auto-dismiss success alerts after 1 second
  if (type === "success") {
    setTimeout(() => {
      dismissAlert()
    }, 1000)
  }
  
  return {
    id,
    dismiss: () => dismissAlert()
  }
}

function dismissAlert() {
  dispatch(null)
}

export function useAlertModal() {
  const [alert, setAlert] = React.useState<AlertData | null>(memoryAlert)
  const isMobile = useIsMobile()

  React.useEffect(() => {
    listeners.push(setAlert)
    return () => {
      const index = listeners.indexOf(setAlert)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    alert: isMobile ? null : alert, // Only show on desktop
    showAlert,
    dismissAlert
  }
}

export { showAlert, dismissAlert }